import React, { useState, useCallback, useRef } from "react";
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Paper,
  Tooltip,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Chip,
  ClickAwayListener,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popper,
  Avatar,
  ListItemAvatar,
} from "@mui/material";
import { enhancePrompt, deletePinnedPromptForAgent } from "../../api/agentApi";
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  PlayArrow as RunIcon,
  AttachFile as AttachFileIcon,
  Clear as ClearIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Description,
  Image as ImageIcon,
} from "@mui/icons-material";
import {
  getAgentRunResultFromUrl,
  createPinnedPromptForAgent,
  listPinnedPromptsForAgent,
  listAllAgentRuns,
  getAgentRunArtifacts,
  API_BASE,
  getAuthToken,
} from "../../api/agentApi";
import { marked } from "marked";
import { getAgent, runAgent } from "../../api/agentApi";
import { useParams } from "react-router-dom";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ThinkingProgressIndicator from "../ThinkingProgressIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PromptEnhancerDialog from "./PromptEnhancerDialog";
import { AgentPinnedPrompt } from "../../types";
import ArtifactsAndPinnedPromptsSideBar from "./ArtifactsAndPinnedPromptsSideBar";
import PinPromptDropdown from "./PinPromptDropdown";

const COPPER = "#B87333";
const COPPER_DARK = "#8A5523";
const COPPER_BORDER = "rgba(184, 115, 51, 0.58)";

const copperChipSx = {
  bgcolor: "transparent",
  border: `1px solid ${COPPER_BORDER}`,
  color: COPPER_DARK,
  "& .MuiChip-label": {
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  "& .MuiChip-icon": {
    color: COPPER_DARK,
  },
  "&:hover": {
    bgcolor: "rgba(184, 115, 51, 0.07)",
    borderColor: COPPER,
  },
};

interface AgentRunDrawerProps {
  agentCard: any;
  open: boolean;
  onClose: () => void;
  onRunComplete?: () => void;
  agentID: string;
  agentName: string;
  canRun?: boolean;
}

// --- Components ---

const PINNED_PROMPT_WIDTH = "30%";
const PROMPT_RESPONSE_WIDTH = "70%";

const AgentRunDrawer: React.FC<AgentRunDrawerProps> = ({
  agentCard,
  agentID,
  agentName,
  open,
  onClose,
  onRunComplete,
  canRun = true,
}) => {
  const [promptText, setPromptText] = useState("");
  const [runStatus, setRunStatus] = useState<any>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [finalResult, setFinalResult] = useState("");
  const [thinkingDots, setThinkingDots] = useState(0);
  const [openaiApiKey, setOpenaiApiKey] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [agent, setAgent] = useState(null);
  // Removed streaming state
  const [typewriterSpeed, setTypewriterSpeed] = useState(20);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [enhancedSuggestion, setEnhancedSuggestion] = useState("");
  const { agentId } = useParams();
  const [pinnedPrompts, setPinnedPrompts] = useState<AgentPinnedPrompt[]>([]);
  const [deleting, setDeleting] = useState("");
  const [error, setError] = useState("");
  const [hasRunTriggered, setHasRunTriggered] = useState(false);
  const pollTimeoutRef = React.useRef<any>(null);
  const dotsIntervalRef = React.useRef<any>(null);
  const isUnmountedRef = React.useRef(false);

  React.useEffect(() => {
    setPromptText("");
  }, []);

  React.useEffect(() => {
    // Reset unmounted flag when drawer opens
    if (open) {
      isUnmountedRef.current = false;
    }

    const fetchPrompts = async () => {
      try {
        const prompts = await listPinnedPromptsForAgent(agentId);
        setPinnedPrompts(prompts || []);
      } catch {
        setPinnedPrompts([]);
      }
    };
    if (open && agentId) {
      fetchPrompts();
    }
  }, [agentId, open]);

  // ****************************
  // Call the enhance prompt API
  // ****************************
  const enhancePromptApi = async (): Promise<string> => {
    try {
      const response = await enhancePrompt({
        role: agentCard?.role,
        description: agentCard?.description,
        input_prompt: promptText,
        tools: agentCard?.tools,
        mode: "zero_shot",
        model: "gpt-4o",
        llm_overrides: null,
      });
      return response.enhanced_prompt;
    } catch (error) {
      console.error("Error enhancing prompt:", error);
      throw new Error("Failed to enhance prompt. Please try again.");
    }
  };

  const handleCopyPrompt = (text: string) => {
    setPromptText(text);
  };

  const handleClearInput = () => {
    setPromptText("");
  };

  const handleDeletePrompt = (promptId: any) => {
    setError("");
    if (!window.confirm(`Delete prompt '${promptId}'? This cannot be undone.`))
      return;
    setDeleting(promptId);
    try {
      deletePinnedPromptForAgent(agentId, promptId);
      setPinnedPrompts(pinnedPrompts.filter((p: any) => p.id !== promptId));
    } catch (e) {
      setDeleting("");
      setError("Failed to delete prompt.");
    }
    setDeleting("");
  };

  const regeneratePrompt = useCallback(async () => {
    if (agentCard) {
      setIsEnhancing(true);
      try {
        // const suggestion = await enhancePromptApi(prompt, agentCard, agentName);
        const suggestion = await enhancePromptApi();
        console.log("suggestion:", suggestion);
        setEnhancedSuggestion(suggestion);
      } catch (error) {
        setEnhancedSuggestion(
          "Error generating enhancement. Please try again.",
        );
      } finally {
        setIsEnhancing(false);
      }
    }
  }, [promptText, agentCard, agentName]);

  const handleEnhanceClick = async () => {
    setIsModalOpen(true);
    await regeneratePrompt();
  };

  const handleAcceptEnhancement = (finalPrompt: string) => {
    setPromptText(finalPrompt); // Replace original prompt
    setIsModalOpen(false); // Close modal
  };

  const handleRejectEnhancement = () => {
    setIsModalOpen(false); // Close modal
  };
  // ******************************************************************
  // File attachment in prompt
  // ******************************************************************
  const [selectedFiles, setSelectedFiles] = useState<FileReference[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterText, setFilterText] = useState("");
  const [fileLibrary, setFileLibrary] = useState<FileReference[]>([]);
  const [isLoadingArtifacts, setIsLoadingArtifacts] = useState(false);
  const [hasLoadedArtifacts, setHasLoadedArtifacts] = useState(false);
  const [loadedRunsCount, setLoadedRunsCount] = useState(0);
  const [totalRunsCount, setTotalRunsCount] = useState(0);
  const [hasMoreRuns, setHasMoreRuns] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const blobUrlsRef = useRef<Set<string>>(new Set()); // Track all created blob URLs

  interface FileReference {
    id: string;
    name: string;
    version: string;
    runNumber: number;
    thumbnail?: string; // URL to the generated thumbnail
  }

  // Fetch all artifacts from all runs when user first types @file
  // Helper function to fetch image securely as blob (prevents token leakage in logs/history)
  const fetchSecureImage = async (url: string): Promise<string | undefined> => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}${url}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error(`Failed to fetch image: ${response.status}`);
        return undefined;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      // Track this blob URL for cleanup on unmount
      blobUrlsRef.current.add(blobUrl);
      
      return blobUrl;
    } catch (error) {
      console.error("Failed to fetch secure image:", error);
      return undefined;
    }
  };

  // Fetch artifacts with pagination - only load INITIAL_RUNS_LIMIT runs at a time
  const INITIAL_RUNS_LIMIT = 20; // Load only 20 most recent runs initially
  const LOAD_MORE_INCREMENT = 20; // Load 20 more runs when user clicks "Load More"
  const CONCURRENCY_LIMIT = 20; // Process 20 runs concurrently

  const fetchAllArtifacts = async (append: boolean = false) => {
    if (isLoadingArtifacts || !agentId) return;

    setIsLoadingArtifacts(true);
    try {
      // Calculate how many runs to fetch
      const limit = append ? LOAD_MORE_INCREMENT : INITIAL_RUNS_LIMIT;
      const offset = append ? loadedRunsCount : 0;

      // Fetch one page to get the most recent runs
      const runsResponse = await (listAllAgentRuns as any)(agentId, {
        page: Math.floor(offset / 200) + 1,
        page_size: 200,
        version: null,
        triggered_by_workflow: false,
      });

      const allRuns = runsResponse.runs || [];
      const totalRuns = runsResponse.total || 0;

      // Slice to only get the runs we want (most recent first)
      const runsToProcess = allRuns.slice(
        offset % 200,
        Math.min((offset % 200) + limit, allRuns.length)
      );

      setTotalRunsCount(totalRuns);
      const newLoadedCount = offset + runsToProcess.length;
      setLoadedRunsCount(newLoadedCount);
      setHasMoreRuns(newLoadedCount < totalRuns);

      // Helper function to check if file is an image
      const isImageFile = (filename: string) => {
        const imageExtensions = [
          ".png",
          ".jpg",
          ".jpeg",
          ".gif",
          ".bmp",
          ".webp",
          ".svg",
        ];
        return imageExtensions.some((ext) =>
          filename.toLowerCase().endsWith(ext),
        );
      };

      // Helper function to process a single run's artifacts
      const processRunArtifacts = async (run: any): Promise<FileReference[]> => {
        try {
          const response = await getAgentRunArtifacts(
            agentId,
            run.agent_version,
            run.run_number,
          );

          const artifacts = response?.artifacts || [];
          if (artifacts.length === 0) return [];

          // Fetch thumbnails concurrently for all images in this run
          const artifactPromises = artifacts.map(async (artifact: any) => {
            const thumbnail = isImageFile(artifact.filename)
              ? await fetchSecureImage(artifact.url)
              : undefined;

            return {
              id: `${run.agent_version}-${run.run_number}-${artifact.filename}`,
              name: artifact.filename,
              version: `${run.agent_version}`,
              runNumber: run.run_number,
              thumbnail: thumbnail,
            };
          });

          return await Promise.all(artifactPromises);
        } catch (err) {
          console.error(
            `Failed to fetch artifacts for run ${run.run_number}:`,
            err,
          );
          return [];
        }
      };

      // Fetch artifacts with bounded concurrency
      const newArtifacts: FileReference[] = [];

      for (let i = 0; i < runsToProcess.length; i += CONCURRENCY_LIMIT) {
        const batch = runsToProcess.slice(i, i + CONCURRENCY_LIMIT);
        const batchResults = await Promise.all(
          batch.map((run: any) => processRunArtifacts(run))
        );
        
        // Flatten and add to results
        batchResults.forEach(artifacts => {
          newArtifacts.push(...artifacts);
        });
      }

      // Append to existing or replace
      if (append) {
        setFileLibrary(prev => [...prev, ...newArtifacts]);
      } else {
        setFileLibrary(newArtifacts);
        setHasLoadedArtifacts(true);
      }

      console.log(
        `Loaded ${newArtifacts.length} artifacts from ${runsToProcess.length} runs (${newLoadedCount}/${totalRuns} total runs)`
      );
    } catch (error) {
      console.error("Failed to fetch artifacts:", error);
      if (!append) {
        setFileLibrary([]);
        setHasLoadedArtifacts(true);
      }
    } finally {
      setIsLoadingArtifacts(false);
    }
  };

  // Load more artifacts when user clicks "Load More"
  const loadMoreArtifacts = () => {
    fetchAllArtifacts(true);
  };

  // Sync selectedFiles with promptText to prevent orphaned file references
  React.useEffect(() => {
    // Remove files from selectedFiles if their names are no longer in the prompt
    setSelectedFiles((current) =>
      current.filter((file) => promptText.includes(file.name)),
    );
  }, [promptText]);

  // Cleanup blob URLs ONLY on component unmount
  React.useEffect(() => {
    return () => {
      // Revoke all blob URLs to prevent memory leaks
      blobUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      blobUrlsRef.current.clear();
    };
  }, []); // Empty dependency array = only runs on unmount

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const val = e.target.value;
    setPromptText(val);

    // Look for the last "@file" anywhere in the string
    const matches = Array.from(val.matchAll(/@file(\w*)/g));
    const match = matches.length > 0 ? matches[matches.length - 1] : null;
    if (match) {
      setFilterText(match[1] || ""); // e.g., if "@filex", search for "x"
      setAnchorEl(inputRef.current);

      // Fetch artifacts on first @file trigger
      if (!hasLoadedArtifacts && !isLoadingArtifacts) {
        fetchAllArtifacts();
      }
    } else {
      setAnchorEl(null);
    }
  };

  const handleFileSelect = (file: FileReference) => {
    const lastTriggerIndex = promptText.lastIndexOf("@file");
    if (lastTriggerIndex !== -1) {
      // 1. Always replace @file with the filename in the text
      const before = promptText.substring(0, lastTriggerIndex);
      const newPromptText = `${before}${file.name} `;
      setPromptText(newPromptText);

      // 2. Track metadata - only add if not already present (prevent duplicate chips)
      // The useEffect will clean up selectedFiles automatically
      if (!selectedFiles.find((f) => f.id === file.id)) {
        setSelectedFiles((prev) => [...prev, file]);
        console.log(`Added file ${file.name} to selected files`);
      } else {
        console.log(
          `File ${file.name} already in selected files - only updating text`,
        );
      }
    }
    setAnchorEl(null);
  };
  const filteredOptions = fileLibrary.filter((f) =>
    f.name.toLowerCase().includes(filterText.toLowerCase()),
  );

  // ******************************************************************

  const handleRun = async () => {
    if (!canRun) return;
    // Ensure unmounted flag is false before starting run
    isUnmountedRef.current = false;

    setRunStatus(null);
    setIsRunning(true);
    setIsPolling(false);
    setFinalResult("");
    setHasRunTriggered(true);
    try {
      // Prepare file references if any files are selected
      const fileReferences =
        selectedFiles.length > 0
          ? selectedFiles.map((f) => ({
              id: f.id,
              name: f.name,
              version: f.version,
              runNumber: f.runNumber,
              thumbnail: f.thumbnail || null,
            }))
          : null;

      if (fileReferences) {
        console.log("Running agent with file references:", fileReferences);
      }

      // Run the agent (with optional file references)
      const result = await runAgent(
        agentId,
        JSON.stringify({ input: promptText }) + "\n",
        openaiApiKey,
        fileReferences as any, // Pass files to runAgent - it will create runcontext.json internally
      );
      setRunStatus(result);
      setSnackbarOpen(true);

      // Poll for final result if result_endpoint exists
      if (result.result_endpoint) {
        console.log(
          "Starting polling for result_endpoint:",
          result.result_endpoint,
        );
        setIsPolling(true);
        setFinalResult("");
        setThinkingDots(0);

        // Animate 'Thinking...' ellipsis (0 to 3 dots)
        dotsIntervalRef.current = setInterval(() => {
          setThinkingDots((prev) => (prev + 1) % 4);
        }, 500);

        // Helper function to stop polling and cleanup
        const stopPolling = () => {
          setIsPolling(false);
          if (dotsIntervalRef.current) {
            clearInterval(dotsIntervalRef.current);
            dotsIntervalRef.current = null;
          }
        };

        // Helper function to refresh agent data (updates number_of_executions)
        const refreshAgent = async () => {
          try {
            const updated = await getAgent(agentID);
            setAgent(updated);
            console.log(
              "Agent data refreshed. New execution count:",
              updated.number_of_executions,
            );
          } catch (err) {
            console.error("Failed to refresh agent data:", err);
          }
        };

        // Recursive polling function
        const pollResult = async () => {
          // Check if component was unmounted
          if (isUnmountedRef.current) {
            console.log("Polling stopped: component unmounted");
            return;
          }

          try {
            const data = (await getAgentRunResultFromUrl(
              result.result_endpoint,
            )) as any;
            console.log("Poll response:", data);

            // Success - stop polling and display result
            if (data.status === "success") {
              stopPolling();

              // Handle different output types
              if (typeof data.output === "string" && data.output.trim()) {
                setFinalResult(data.output);
              } else if (data.output && typeof data.output === "object") {
                setFinalResult(JSON.stringify(data.output, null, 2));
              } else {
                setFinalResult("");
              }

              // Refresh agent data to update number_of_executions for next run
              await refreshAgent();

              return;
            }

            // Error - stop polling and show error
            if (data.status === "error") {
              console.error("Agent run failed:", data);
              stopPolling();
              setRunStatus({
                status: "error",
                message: data.message || "Agent execution failed",
                output: data.output,
              });
              return;
            }

            // Still pending - continue polling
            console.log("Still pending, polling again in 2s...");
          } catch (err) {
            console.error("Error polling result:", err);
          }

          // Schedule next poll
          pollTimeoutRef.current = setTimeout(pollResult, 2000);
        };

        // Start polling
        pollResult();
      }
    } catch (e) {
      setRunStatus({ status: "error", message: "Failed to run agent." });
      setSnackbarOpen(true);
      setHasRunTriggered(false);
    } finally {
      setIsRunning(false);
    }
  };

  const handleDrawerClose = () => {
    // Set unmounted flag to stop any ongoing polling
    isUnmountedRef.current = true;

    // Clear polling timeout
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }

    // Clear thinking dots interval
    if (dotsIntervalRef.current) {
      clearInterval(dotsIntervalRef.current);
      dotsIntervalRef.current = null;
    }

    // Reset polling state
    setIsPolling(false);
    setIsRunning(false);
    setRunStatus(null);
    setFinalResult("");
    setThinkingDots(0);
    setAnchorEl(null);

    // Call parent onClose
    onClose();

    // Trigger onRunComplete if a run was triggered
    if (hasRunTriggered) {
      onRunComplete?.();
      setHasRunTriggered(false);
    }
  };

  const handlePinPrompt = async (visibility: any) => {
    const textToPin = promptText.trim();
    if (!textToPin) {
      console.error("Cannot pin empty prompt");
      return;
    }
    try {
      const promptData = {
        prompt_text: textToPin,
        is_public: visibility?.toLowerCase() === "public",
      };

      const result = await createPinnedPromptForAgent(agentId, promptData);
      console.log("Prompt pinned successfully:", result);

      // // Refresh the pinned prompts list
      setPinnedPrompts((prev) => [...prev, result]);
    } catch (error) {
      console.error("Failed to pin prompt:", error);
    }
  };

  const getHelpText = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 0.5, // Space between hint and your textfield
          px: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.75rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          Type&nbsp;
          <Box
            component="span"
            sx={{
              color: "#b87333",
              bgcolor: "rgba(184, 115, 51, 0.1)",
              px: 0.6,
              py: 0.1,
              borderRadius: 1,
              fontFamily: "monospace",
              fontWeight: "bold",
              mr: 0.5,
            }}
          >
            @file
          </Box>
          to reference files
        </Typography>
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      PaperProps={{
        sx: {
          width: "90%",
          height: "100%",
          maxWidth: "100vw",
          maxHeight: "100vh",
          pt: 0,
        },
      }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", height: "95%", pt: 9 }}
      >
        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            overflow: "hidden",
            flexDirection: "row-reverse",
            position: "relative",
            pt: 2,
          }}
          style={{ border: "solid 2px red" }}
        >
          <ArtifactsAndPinnedPromptsSideBar
            prompts={pinnedPrompts}
            onCopy={handleCopyPrompt}
            onDelete={handleDeletePrompt}
            deleting={deleting}
            error={error}
          />
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              px: 3,
              boxSizing: "border-box",
              position: "relative",
              width: PROMPT_RESPONSE_WIDTH,
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                mt: 1,
                mb: 0,
                display: "flex",
                justifyContent: "space-between",
                pb: 1,
              }}
            >
              <Stack
                direction="column"
                spacing={1}
                sx={{ mt: 1 }}
                flexWrap="wrap"
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 0.5, fontWeight: 540 }}
                >
                  Agent
                  ID:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  {agentID}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 540 }}>
                  Agent Name:&nbsp;&nbsp;&nbsp;{agentName}
                </Typography>
              </Stack>
              <Tooltip title="Close Drawer">
                <IconButton onClick={handleDrawerClose} size="large">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Divider sx={{ mb: 0 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
                mt: 9,
                position: "absolute",
                top: "16px",
                left: "24px",
                right: "24px",
                zIndex: 6,
                // border: "1px solid red",
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Run Agent
              </Typography>
              {getHelpText()}
            </Box>

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 5 }}
            >
              <Box ref={inputRef}>
                <TextField
                  multiline
                  minRows={4}
                  fullWidth
                  placeholder="Type your prompt or paste from pinned prompts... type @file to reference a file"
                  value={promptText}
                  onChange={handleInputChange}
                  variant="outlined"
                  sx={{
                    "& .MuiInputBase-input::placeholder": {
                      color: "#707070ff", 
                      opacity: 0.9, // Overrides MUI's default transparent look
                    },
                    // If you are using 'multiline'
                    "& .MuiOutlinedInput-input::placeholder": {
                      color: "#707070ff",
                      opacity: 0.9,
                    },
                  }}
                />
              </Box>

              <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{ zIndex: 1300 }}
                modifiers={[{ name: "offset", options: { offset: [0, 10] } }]}
              >
                <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                  <Paper
                    sx={{
                      mt: 1,
                      border: "1px solid #444",
                      minWidth: 360,
                      maxHeight: 450,
                      overflowY: "auto",
                    }}
                  >
                    <List dense>
                      {isLoadingArtifacts ? (
                        <ListItem>
                          <ListItemText
                            primary="Loading artifacts..."
                            sx={{ color: "#888", textAlign: "center" }}
                          />
                        </ListItem>
                      ) : filteredOptions.length > 0 ? (
                        filteredOptions.map((file) => (
                          <ListItem
                            disablePadding
                            key={file.id}
                            divider
                            sx={{ borderColor: "#444" }}
                          >
                            <ListItemButton
                              onClick={() => handleFileSelect(file)}
                            >
                              <ListItemAvatar sx={{ minWidth: 50 }}>
                                <Avatar
                                  variant="rounded"
                                  src={file.thumbnail}
                                  sx={{
                                    bgcolor: file.thumbnail
                                      ? "transparent"
                                      : "primary.main",
                                    width: 40,
                                    height: 40,
                                    border: file.thumbnail
                                      ? "1px solid #e0e0e0"
                                      : "none",
                                  }}
                                  imgProps={{
                                    sx: {
                                      objectFit: "cover",
                                    },
                                  }}
                                >
                                  {!file.thumbnail && (
                                    <Description fontSize="small" />
                                  )}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={file.name}
                                primaryTypographyProps={{
                                  fontWeight: "medium",
                                }}
                                secondary={
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#888" }}
                                  >
                                    Version:{" "}
                                    <span style={{ color: "#8d6e63" }}>
                                      v{file.version}
                                    </span>{" "}
                                    • Run: #{file.runNumber}
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText
                            primary="No previous artifacts found"
                            sx={{ color: "#888", textAlign: "center" }}
                          />
                        </ListItem>
                      )}
                      
                      {/* Load More Button and Status */}
                      {hasLoadedArtifacts && !isLoadingArtifacts && (
                        <ListItem sx={{ flexDirection: "column", gap: 1, py: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#888", textAlign: "center" }}
                          >
                            Showing artifacts from {loadedRunsCount} of {totalRunsCount} runs
                            {filteredOptions.length > 0 && ` (${filteredOptions.length} files)`}
                          </Typography>
                          {hasMoreRuns && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                loadMoreArtifacts();
                              }}
                              sx={{ textTransform: "none" }}
                            >
                              Load More Runs
                            </Button>
                          )}
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </ClickAwayListener>
              </Popper>

              {/* Visual Badges for JSON Tracking */}
              {(() => {
                const includedFiles = selectedFiles.filter((f) =>
                  promptText.includes(f.name),
                );
                const maxVisible = 3;
                const visibleFiles = includedFiles.slice(0, maxVisible);
                const remainingCount = includedFiles.length - maxVisible;

                return includedFiles.length > 0 ? (
                  <Box
                    sx={{
                      mt: 1.5,
                      pt: 0.5,
                      pb: 1.5,
                      px: 1.5,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      bgcolor: "rgba(0, 0, 0, 0.02)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666",
                        fontWeight: 500,
                        mb: 0.5,
                        display: "block",
                      }}
                    >
                      Files included in the prompt
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {visibleFiles.map((file) => (
                        <Chip
                          key={file.id}
                          label={file.name}
                          size="small"
                          variant="outlined"
                          avatar={
                            file.thumbnail ? (
                              <Avatar
                                src={file.thumbnail}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  border: "1px solid #e0e0e0",
                                }}
                              />
                            ) : undefined
                          }
                          icon={
                            !file.thumbnail ? (
                              <Description
                                sx={{ fontSize: "14px", color: "primary.main" }}
                              />
                            ) : undefined
                          }
                          sx={{
                            ...copperChipSx,
                          }}
                        />
                      ))}
                      {remainingCount > 0 && (
                        <Tooltip
                          title={
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600 }}
                              >
                                Additional files:
                              </Typography>
                              {includedFiles.slice(maxVisible).map((file) => (
                                <Box
                                  key={file.id}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mt: 0.5,
                                  }}
                                >
                                  {file.thumbnail ? (
                                    <Avatar
                                      src={file.thumbnail}
                                      sx={{
                                        width: 16,
                                        height: 16,
                                        mr: 0.5,
                                        border: "1px solid #e0e0e0",
                                      }}
                                    />
                                  ) : (
                                    <Description
                                      sx={{ fontSize: 16, mr: 0.5 }}
                                    />
                                  )}
                                  <Typography variant="caption">
                                    {file.name}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          }
                          arrow
                        >
                          <Chip
                            label={`+${remainingCount} more`}
                            size="small"
                            variant="outlined"
                            icon={<AttachFileIcon sx={{ fontSize: "14px" }} />}
                            sx={{
                              ...copperChipSx,
                              borderStyle: "dashed",
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                ) : null;
              })()}
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                mb={2}
                mt={1}
                gap={6}
              >
                <PinPromptDropdown
                  onPin={handlePinPrompt}
                  disabled={!promptText.trim()}
                />
                <Stack direction="row" spacing={4} justifyContent="flex-end">
                  <Button
                    onClick={handleEnhanceClick}
                    startIcon={<AutoFixHighIcon />}
                    variant="outlined"
                  >
                    {isEnhancing
                      ? "Enhancing..."
                      : !promptText.trim()
                        ? "Suggest Prompt"
                        : "Enhance Prompt"}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<RunIcon />}
                    onClick={handleRun}
                    disabled={isRunning || !promptText.trim()}
                    sx={{ borderRadius: "99px", textTransform: "none" }}
                  >
                    {isRunning ? "Running..." : "Run Agent"}
                  </Button>
                </Stack>
              </Box>
            </Box>
            <Typography variant="body1" fontWeight="bold" mb={0.25}>
              Response
            </Typography>
            <Paper
              elevation={1}
              sx={{
                flexGrow: 1,
                mt: 1,
                mb: 2,
                p: 2,
                bgcolor: "white",
              }}
            >
              {isRunning && <ThinkingProgressIndicator />}
              {isPolling && (
                <Box sx={{ mt: 2, p: 2, borderRadius: 1 }}>
                  <Box
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      m: 0,
                    }}
                  >
                    <Box sx={{ fontSize: 18, color: "#555", fontWeight: 500 }}>
                      {`Thinking${".".repeat(thinkingDots)}`}
                    </Box>
                  </Box>
                </Box>
              )}
              {!isPolling && finalResult && (
                <Box sx={{ my: 1, px: 2, borderRadius: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Final Answer
                  </Typography>
                  <Box
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      m: 0,
                    }}
                  >
                    <Box
                      sx={{
                        "& p": { mt: 0, mb: 1 },
                        "& ul, & ol": { mt: 0, mb: 0.5, pl: 3 },
                        "& li": { mb: 0.5 },
                        "& pre": {
                          bgcolor: "#f5f5f5",
                          p: 1,
                          borderRadius: 1,
                          overflow: "auto",
                        },
                        "& code": {
                          bgcolor: "#f5f5f5",
                          px: 0.5,
                          borderRadius: 0.5,
                          fontSize: "0.9em",
                        },
                      }}
                    >
                        <span
                          dangerouslySetInnerHTML={{
                          __html: marked.parse(finalResult, { async: false }),
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              )}
              {runStatus && runStatus.output && (
                <Accordion sx={{ mt: 2, borderRadius: 1, boxShadow: "none" }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Output (raw agent output)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      component="pre"
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        m: 0,
                      }}
                    >
                      {runStatus.output}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
              {runStatus &&
                runStatus.status === "error" &&
                (runStatus.message || runStatus.output) && (
                  <Accordion
                    sx={{
                      mt: 2,
                      background: "#ffebee",
                      borderRadius: 1,
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#b71c1c" }}
                      >
                        Error (click to expand)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        component="pre"
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          m: 0,
                          color: "#b71c1c",
                        }}
                      >
                        {runStatus.message}
                        {runStatus.output &&
                          (typeof runStatus.output === "string"
                            ? `\n${runStatus.output}`
                            : "")}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}
              <PromptEnhancerDialog
                agent={agentCard}
                originalPrompt={promptText}
                enhancedPrompt={enhancedSuggestion}
                isEnhancing={isEnhancing}
                isOpen={isModalOpen}
                onAccept={handleAcceptEnhancement}
                onReject={handleRejectEnhancement}
                onRegenerate={regeneratePrompt}
              />
            </Paper>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AgentRunDrawer;
